import { FormModal } from '~/shared/forms/FormModal';
import { getInterventionDefinitionInitialValues } from '~/libraries/interventions/definition/values';
import { getInterventionDefinitionValidationSchema } from '~/libraries/interventions/definition/validation';
import { InterventionDefinitionInfo } from '~/libraries/interventions/definition/InterventionDefinitionInfo';
import { InterventionDefinitionFormBody } from '~/libraries/interventions/definition/InterventionDefinitionFormBody';
import type { FormValues } from '~/shared/forms/types';
import { editPredefinedInterventionOptions, type EditPredefinedInterventionCommand, type FullOrderViewModel } from '@generated';
import { convertClientValuesToServerValues } from '~/libraries/forms/values';
import { useGoalsAndnterventionsForOrder } from '../queries';
import { useSuspenseQuery } from '@tanstack/react-query';

export function EditPredefinedInterventionFormModal({
	interventionId,
	fullOrder,
	closeModal
}: {
	interventionId: string;
	fullOrder: FullOrderViewModel;
	closeModal: () => void;
}) {
	const intervention = fullOrder.interventions.find(intervention => intervention.id === interventionId);
	if (!intervention) {
		throw new Error('Draft intervention not found');
	} else if (!intervention.interventionDefinition) {
		throw new Error('Draft intervention definition not found');
	}
	const { refetch } = useSuspenseQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const initialValues = getInterventionDefinitionInitialValues(
		intervention.interventionDefinition,
		intervention.assignedDisciplines.map(discipline => discipline.assignedDisciplineId),
		fullOrder.certificationPeriod.endDate,
		intervention.definitionValues
	);
	const validationSchema = getInterventionDefinitionValidationSchema(intervention.interventionDefinition, fullOrder.certificationPeriod.endDate);

	const onSaveTransform = (values: FormValues): EditPredefinedInterventionCommand => {
		return {
			orderId: fullOrder.id,
			interventionId: intervention.id,
			notes: values?.notes,
			projectedStartDate: values?.projectedStartDate,
			projectedEndDate: values?.projectedEndDate,
			homeHealthAideInterventionFrequencyTypeId: values?.homeHealthAideInterventionFrequencyTypeId,
			homeHealthAideInterventionFrequencyTypeOther: values?.homeHealthAideInterventionFrequencyTypeOther,
			assignedDisciplineIds: values?.assignedDisciplineIds,
			interventionDefinitionValues: intervention.interventionDefinition?.hasDefinitionFields
				? convertClientValuesToServerValues(values, intervention.interventionDefinition.definitionFormFields)
				: undefined
		};
	};

	return (
		<FormModal
			title="Edit Intervention"
			closeModal={closeModal}
			initialValues={initialValues}
			saveMutation={editPredefinedInterventionOptions}
			onSuccess={() => {
				refetch();
				closeModal();
			}}
			onSaveTransform={onSaveTransform}
			validationSchema={validationSchema}
			size="lg"
		>
			<div className="grid grid-cols-1 gap-4">
				<InterventionDefinitionInfo interventionDefinition={intervention.interventionDefinition} interventionStatus="Drafted" />
				<InterventionDefinitionFormBody interventionDefinition={intervention.interventionDefinition} />
			</div>
		</FormModal>
	);
}
